/**
 * FeedbackPublicPage — /opine/:token (sem login)
 * Feedback dos desistentes via link enviado por e-mail/WhatsApp.
 * Responder reativa o teste por +7 dias e convida para as novidades/planos.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { StarRating, REASON_OPTIONS } from './ExperienceFeedbackCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FeedbackPublicPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState('loading'); // loading | form | used | invalid | success
  const [info, setInfo] = useState(null);
  const [rating, setRating] = useState(0);
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/feedback/public/${token}`)
      .then((r) => {
        setInfo(r.data);
        setState(r.data.used ? 'used' : 'form');
      })
      .catch(() => setState('invalid'));
  }, [token]);

  const submit = async () => {
    if (!rating && !reason && !message.trim()) {
      toast.error('Escolha um motivo, dê uma nota ou escreva uma mensagem');
      return;
    }
    setSending(true);
    try {
      const r = await axios.post(`${API}/feedback/public/${token}`, {
        rating: rating || null,
        reason: reason || null,
        message,
      });
      setResult(r.data);
      setState('success');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao enviar feedback');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-800 to-green-950 flex items-center justify-center p-4" data-testid="public-feedback-page">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 sm:p-8">
        <div className="text-center mb-5">
          <div className="text-4xl mb-1">🌳</div>
          <h1 className="text-xl font-bold text-green-800">HeveaTalent</h1>
        </div>

        {state === 'loading' && <p className="text-center text-gray-500">Carregando...</p>}

        {state === 'invalid' && (
          <div className="text-center" data-testid="public-feedback-invalid">
            <p className="text-lg font-semibold text-gray-800">Link inválido ou expirado</p>
            <p className="text-sm text-gray-500 mt-2">Entre em contato conosco ou acesse a plataforma.</p>
            <Button className="mt-4 bg-green-600 hover:bg-green-700" onClick={() => navigate('/')}>Ir para o HeveaTalent</Button>
          </div>
        )}

        {state === 'used' && (
          <div className="text-center" data-testid="public-feedback-used">
            <p className="text-lg font-semibold text-gray-800">Você já respondeu — obrigado! 🙏</p>
            <p className="text-sm text-gray-600 mt-2">Seu feedback nos ajuda a melhorar a plataforma.</p>
            <Button className="mt-4 bg-green-600 hover:bg-green-700" onClick={() => navigate('/')} data-testid="public-feedback-go-home">
              Entrar na plataforma
            </Button>
          </div>
        )}

        {state === 'form' && (
          <div>
            <h2 className="text-lg font-bold text-gray-800">
              {info?.name ? `Olá, ${info.name.split(' ')[0]}!` : 'Olá!'} Sentimos sua falta 💚
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Seu período de teste terminou e queremos muito saber o que aconteceu. Sua opinião vale ouro para melhorarmos.
            </p>
            <p className="text-sm font-semibold text-green-700 mt-2" data-testid="public-feedback-reward-offer">
              🎁 Responda e ganhe <span className="underline">+{info?.reward_days || 7} dias de teste grátis</span> — seu acesso volta na hora!
            </p>

            <p className="text-sm font-semibold text-gray-700 mt-5 mb-2">O que mais pesou na sua decisão?</p>
            <div className="flex flex-wrap gap-2" data-testid="public-feedback-reasons">
              {REASON_OPTIONS.map((r) => (
                <button
                  key={r.key}
                  type="button"
                  data-testid={`public-feedback-reason-${r.key}`}
                  onClick={() => setReason(reason === r.key ? '' : r.key)}
                  className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${
                    reason === r.key
                      ? 'bg-green-700 text-white border-green-700'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-green-500'
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>

            <p className="text-sm font-semibold text-gray-700 mt-5 mb-2">Que nota você dá para a plataforma?</p>
            <StarRating rating={rating} setRating={setRating} />

            <p className="text-sm font-semibold text-gray-700 mt-5 mb-2">Conte sua dificuldade ou experiência</p>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Críticas, sugestões, o que faltou... pode falar à vontade!"
              className="w-full border rounded-md p-2 text-sm min-h-[90px]"
              data-testid="public-feedback-message"
            />

            <Button
              className="w-full mt-4 bg-green-600 hover:bg-green-700"
              onClick={submit}
              disabled={sending}
              data-testid="public-feedback-submit"
            >
              {sending ? 'Enviando...' : 'Enviar feedback e ganhar +7 dias'}
            </Button>
          </div>
        )}

        {state === 'success' && (
          <div data-testid="public-feedback-success">
            <h2 className="text-lg font-bold text-green-800">Obrigado pelo seu feedback! 🙏</h2>
            {result?.reward?.granted ? (
              <p className="text-green-700 mt-2 font-semibold" data-testid="public-feedback-reward-msg">
                🎁 Seu acesso foi reativado por +{result.reward.days} dias a partir de hoje!
              </p>
            ) : (
              <p className="text-gray-600 mt-2">Sua opinião nos ajuda a construir uma plataforma melhor.</p>
            )}
            <p className="text-sm text-gray-700 mt-4 font-semibold">Veja o que tem de novo esperando por você:</p>
            <ul className="text-sm text-gray-700 mt-1 space-y-1">
              {(result?.novidades || []).map((n, i) => (
                <li key={i}>{n}</li>
              ))}
            </ul>
            <Button
              className="w-full mt-5 bg-green-600 hover:bg-green-700"
              onClick={() => navigate('/')}
              data-testid="public-feedback-login"
            >
              Entrar agora e aproveitar
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
